import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';

import { api_client } from '@src/bootstrap';
import { EU_COUNTRIES } from '@src/constants';
import { useAppSelector } from '@src/hooks';
import { useGlobalContext } from '@src/hooks/useGlobalContext';
import { USER } from '@src/services/user';
import { getCountryCode } from '@src/utils/getters';
import { handleLogout } from '@src/utils/user';

export type OnboardingAnswer = {
    id: number;
    answer: string;
    idx: number;
    questionId: number;
};

export type OnboardingQuestion = {
    id: number;
    question: string;
    answers: OnboardingAnswer[];
};

export type GetOnboardingQuestionsResponse = {
    getOnboardingQuestionsAndAnswers: OnboardingQuestion[];
};

export const useOnboardingQuestions = (): {
    show: boolean;
    setShow: (val: boolean) => void;
    handleSubmitClick: () => void;
    answers: Record<
        number | 'declaration',
        { option: string | null; other_text?: string | null }
    >;
    setAnswers: React.Dispatch<
        React.SetStateAction<
            Record<
                number | 'declaration',
                { option: string | null; other_text?: string | null }
            >
        >
    >;
    compliance_questions: { title: string; options: string[] }[];
    loading: boolean;
    submissionSuccess: boolean;
    setSubmissionSuccess: React.Dispatch<React.SetStateAction<boolean>>;
    handleLogOut: () => void;
} => {
    const { user } = useAppSelector((state) => state.auth);
    const router = useRouter();
    const timeout_ref = useRef<NodeJS.Timeout | null>(null);

    const [loading, setLoading] = useState(true);
    const [questions_data, setQuestionsData] = useState<OnboardingQuestion[]>(
        [],
    );
    const [show, setShow] = useState(false);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const [answers, setAnswers] = useState<
        Record<
            number | 'declaration',
            { option: string | null; other_text?: string | null }
        >
    >({
        declaration: { option: null },
    });
    const [is_on_onboarding_flow, setIsOnOnboarding] = useState(false);
    const { has_eu_restriction, showKycCompleted } = useGlobalContext();

    const go_live_date = dayjs('2025-04-28').startOf('day');

    const is_new_user = useMemo(() => {
        if (!user?.created_at) return false;

        const created_date = dayjs(Number(user?.created_at) * 1000);

        return (
            created_date.isAfter(go_live_date) ||
            created_date.isSame(go_live_date)
        );
    }, [user?.created_at, go_live_date]);

    const handleLogOut = () => {
        handleLogout(router);
        setShow(false);
        setSubmissionSuccess(false);

        const reset_answers: Record<
            number | 'declaration',
            { option: string | null; other_text?: string | null }
        > = { declaration: { option: null } };

        questions_data.forEach((_, index) => {
            reset_answers[index] = { option: null };
        });

        setAnswers(reset_answers);
    };

    const fetchQuestions = async () => {
        try {
            const result = await api_client.query({
                query: USER.GET_QUESTIONS_AND_ANSWERS,
                fetchPolicy: 'no-cache',
            });

            const data = result as GetOnboardingQuestionsResponse;
            if (data?.getOnboardingQuestionsAndAnswers?.length) {
                setQuestionsData(data.getOnboardingQuestionsAndAnswers);
            }
        } catch (err) {
            console.log('Error fetching questions:', err);

            if (err instanceof Error) {
                console.error('Error message:', err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    // fetch questions when new user enters onboarding flow
    useEffect(() => {
        if (!is_new_user || !user) return;

        if (user.onboarding_answers?.length > 0) return;

        fetchQuestions();
    }, [is_new_user, user]);

    useEffect(() => {
        const is_on_onboarding_path = router.asPath.includes('onboarding');
        setIsOnOnboarding(is_on_onboarding_path);
    }, [router.asPath]);

    // show modal after veriff
    useEffect(() => {
        if (timeout_ref.current) {
            clearTimeout(timeout_ref.current);
        }

        timeout_ref.current = setTimeout(() => {
            const is_on_onboarding_path = router.asPath.includes('onboarding');

            const no_answer_yet =
                !user?.onboarding_answers ||
                user.onboarding_answers.length === 0;

            const should_show =
                !loading &&
                user &&
                is_new_user &&
                !is_on_onboarding_path &&
                no_answer_yet &&
                !showKycCompleted &&
                questions_data.length > 0;

            if (should_show) {
                setShow(true);

                const initial_answers: Record<
                    number | 'declaration',
                    { option: string | null; other_text?: string | null }
                > = { declaration: { option: null } };

                questions_data.forEach((_, idx) => {
                    initial_answers[idx] = { option: null };
                });

                setAnswers(initial_answers);
            }
        }, 500);

        return () => {
            if (timeout_ref.current) clearTimeout(timeout_ref.current);
        };
    }, [
        user,
        loading,
        questions_data,
        router.asPath,
        is_on_onboarding_flow,
        is_new_user,
        showKycCompleted,
    ]);

    const compliance_questions = useMemo(
        () =>
            questions_data.map((q) => ({
                title: q.question,
                options: q.answers.map((a) => a.answer),
            })),
        [questions_data],
    );

    const handleSubmitClick = async () => {
        try {
            const prepared_answers = Object.entries(answers)
                .map(([key, value]) => {
                    const index = Number(key);
                    const question = questions_data[index];
                    if (!question) return null;

                    const selected_answer = question.answers.find(
                        (a) => a.answer === value.option,
                    );
                    if (!selected_answer) return null;

                    return {
                        questionId: question.id,
                        answerId: selected_answer.id,
                    };
                })
                .filter(
                    (a): a is { questionId: number; answerId: number } => !!a,
                );

            if (prepared_answers.length !== questions_data.length) {
                console.log(
                    'Not all questions were answered. Aborting submission.',
                );
                return;
            }

            const result = await api_client.mutate({
                mutation: USER.SUBMIT_ONBOARDING_ANSWERS,
                variables: { answers: prepared_answers },
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const submitted = (result as any).submitOnboardingAnswers;

            if (submitted?.length > 0) {
                setSubmissionSuccess(true);
                setShow(false);

                const users_country_code = getCountryCode(user?.country);
                const is_eu_country = users_country_code
                    ? EU_COUNTRIES.has(users_country_code)
                    : false;

                if (is_eu_country && has_eu_restriction) {
                    router.push('/country-not-supported');
                } else {
                    router.push('/');
                }
            } else {
                console.log('Submission failed!');
            }
        } catch (err) {
            console.error('Error submitting answers:', err);
        }
    };

    return {
        show,
        setShow,
        handleSubmitClick,
        answers,
        setAnswers,
        compliance_questions,
        loading,
        submissionSuccess,
        setSubmissionSuccess,
        handleLogOut,
    };
};
